export interface ApiListResponse<T> {
  total: number;
  data: T[];
}

/* eslint-disable @typescript-eslint/naming-convention */
interface SubmissionSiteData {
  data: string[];
}

/** Represents a document task instance record for a given document task */
type TDocumentTaskInstance = {
  /** primary key of the document task instance */
  id: string;
  /** title of the document task instance */
  title: string;
  /** description of the document task instance */
  description: string;
};
/** Represents a document task record for a given document */
type TDocumentTask = {
  /** primary key of the document task instance */
  id: string;
  /** primary key of the task instance */
  task_instance_id: string;
  /** primary key of the document */
  document_id: string;
  /** task instance for the document task */
  task_instance?: TDocumentTaskInstance;
};

export interface Document {
  id: string;
  title: string;
  name?: string;
  data: DocumentData;
  is_complete: boolean;
  is_response: boolean;
  is_outcome: boolean;
  is_additional: boolean;
  complete_timestamp: null;
  created_by_user: CreatedByUser;
  is_reviewer_document: boolean;
  document_type: RequiredDocumentType;
  attribute_type: DocumentAttributeType;
  entity_type: string;
  entity_id: string;
  dataItem?: any;
  created_dt?: string;
  updated_dt?: string;
  reviewed_for?: string;
  messages?: number;
  document?: any;
  is_resolved?: boolean;
  required?: boolean;
  sites?: SubmissionSiteData;
  wcg_name?: string;
  tasks?: TDocumentTask[];
}

export interface InProgressDocument {
  id: string;
  name: string;
  progress: number;
  uploading: boolean;
  error?: string | null;
  is_additional: boolean;
  complete_timestamp?: string;
  documentTypeId: string;
  documentTypeName: string;
  documentName: string;
  document?: Document | null;
  questionId?: string;
  required?: boolean;
}

export interface CreatedByUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

export interface DocumentData {
  required_document_id: string;
  title: string;
  id: string;
  file_id: string;
  new_title: string;
  file_size: string;
  entity_id: string;
  entity_type: string;
  original_title: string;
  data: Record<string, unknown>; // Assuming 'data' is an object with unknown structure
  created_dt: string;
  updated_dt: string;
  version_id: string;
  mime_type: Mime;
  thumbnails?: any[];
  source_document_title?: string;
  reviewed_for?: string;
  reviewed_date?: string;
  transmitted_date?: string;
}

interface Mime {
  id: string;
  name: string;
  extension: string;
}

export type DocumentApiResponse = ApiListResponse<Document>;

export interface RequiredDocumentType {
  id: string;
  name: string;
  optional: boolean;
  document_type: DocumentType;
  document_name?: string;
  document_type_id?: string;
  document_type_name?: string;
  workspace_type: {
    id: string;
    name: string;
    description: string;
  };
  created_dt: string;
  updated_dt: string;
  questionId?: string;
  answerId?: string;
}

export interface DocumentType {
  id: string;
  is_active: boolean;
  name: string;
  title?: string; // Title is computed and does not come from the API
  reviewer_document_type: boolean;
  document_type?: DocumentType;
  questionId?: string; // Connects the doc type to the question so we can identify which question should get the document when multiple questions require the same doc type
  answerId?: string;
  created_dt: string;
  updated_dt: string;
  outcome: boolean;
  submission: boolean;
}

export interface DocumentAttributeType {
  id: string;
  name: string;
  description: string;
}

export interface DocumentTypeFilter {
  id: string;
  name: string;
}

export type RequiredDocumentTypeApiResponse =
  ApiListResponse<RequiredDocumentType>;

export type DocumentTypeApiResponse = ApiListResponse<DocumentType>;

export interface DocumentMimeType {
  id: string;
  name: string;
  extension: string;
}

export type DocumentMimeTypeApiResponse = ApiListResponse<DocumentMimeType>;
/* eslint-disable @typescript-eslint/naming-convention */

export interface DocumentListItem {
  id: string;
  name: string;
  qcStatus: string;
  title: string;
  docRequired: string;
  isUploaded: boolean;
  is_additional: boolean;
  attribute?: string;
  attribute_type_name?: string;
  created_dt: string;
  updated_dt: string;
  is_complete: boolean;
  complete_timestamp?: string;
  document_type?: DocumentType | string;
  document_type_id: string;
  document_type_name?: string;
  document_name?: string;
  file_name?: string;
  dataItem?: any;
  uploadProgress?: number;
  isUploading?: boolean;
  isError?: boolean;
  error?: string;
  document?: Document | null;
  uploadSuccess?: boolean;
  entity_type?: string;
  entity_id?: string;
  messages?: number;
  wcg_name?: string;
}

export interface AttributeType {
  id: string;
  name: string;
}

export function isInProgressDocument(
  doc: Document | InProgressDocument | DocumentType,
): doc is InProgressDocument {
  return (doc as InProgressDocument).progress !== undefined;
}

export function isDocument(
  doc: Document | DocumentType | InProgressDocument,
): doc is Document {
  return 'title' in doc;
}

export function isDocumentType(
  doc: Document | DocumentType | InProgressDocument,
): doc is DocumentType {
  return (
    ('optional' in doc || 'is_active' in doc) && !('documentTypeId' in doc)
  );
}

export function filterDocuments(
  docs: Array<Document | InProgressDocument>,
): Document[] {
  return docs.filter((doc) => !isInProgressDocument(doc)) as Document[];
}

export interface UploadStatusItem {
  index: number;
  isError: string;
  isUploadInProgress: boolean;
  progressCount: number;
  uploadFileName: string;
}
